import { trackElementInteraction } from 'frontend/shared/javascripts/util/analytics'

const ARIA_ATTRIBUTE_NAME = 'aria-expanded'
const EXPANDED_TRUE_VALUE = 'true'
const EXPANDED_FALSE_VALUE = 'false'

up.compiler('[data-dropdown]', (element) => {
  const toggle = element.querySelector<HTMLButtonElement>('[data-dropdown-toggle]')!
  const menu = element.querySelector<HTMLElement>('[data-dropdown-menu]')!
  const openOnHover = 'dropdownOpenOnHover' in element.dataset
  const highlightCurrent = 'dropdownHighlightCurrent' in element.dataset
  // Touch devices have no real hover, but browsers often fire a synthetic mouseenter on tap as
  // a compatibility fallback — without this check, a tap would also trigger the hover handlers
  // below, fighting with the click handler for the same open/close state.
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)')
  let hoverCloseTimeout: ReturnType<typeof setTimeout> | undefined

  menu.dataset.focusTrap = 'true'
  up.hello(menu)

  toggle.setAttribute('aria-haspopup', 'true')
  menu.setAttribute('role', 'dialog')

  function isExpanded() {
    return toggle.getAttribute(ARIA_ATTRIBUTE_NAME) === EXPANDED_TRUE_VALUE
  }

  function setExpanded(isExpanded: boolean) {
    toggle.setAttribute(
      ARIA_ATTRIBUTE_NAME,
      isExpanded ? EXPANDED_TRUE_VALUE : EXPANDED_FALSE_VALUE,
    )
    toggle.classList.toggle('active', isExpanded) // For Bootstrap elements
    toggle.classList.toggle('-active', isExpanded)
    menu.classList.toggle('-show', isExpanded)
    menu.ariaModal = isExpanded.toString()
    up.emit(menu, 'app.focusTrap.focus')
  }

  function show() {
    if (isExpanded()) return

    trackElementInteraction(toggle, 'dropdown.toggle', { dropdownExpanded: true })
    setExpanded(true)
  }

  function hide() {
    if (!isExpanded()) return

    trackElementInteraction(toggle, 'dropdown.toggle', { dropdownExpanded: false })
    setExpanded(false)
  }

  function updateCurrent() {
    if (!highlightCurrent) return

    const currentLinks = Array.from(menu.querySelectorAll('.dropdown--link.-active'))
    toggle.classList.toggle(
      '-current',
      currentLinks.some((link) => getComputedStyle(link).display !== 'none'),
    )
  }

  function onBodyClick(event: MouseEvent) {
    if (!element.contains(event.target as HTMLElement)) hide()
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isExpanded()) {
      hide()
      toggle.focus()
    }
  }

  function onMouseEnter() {
    if (!openOnHover || !hoverCapable.matches) return
    clearTimeout(hoverCloseTimeout)
    show()
  }

  function onMouseLeave() {
    if (!openOnHover || !hoverCapable.matches) return
    hoverCloseTimeout = setTimeout(hide, 100)
  }

  element.addEventListener('click', () => (isExpanded() ? hide() : show()))
  element.addEventListener('mouseenter', onMouseEnter)
  element.addEventListener('mouseleave', onMouseLeave)
  menu.addEventListener('keydown', onKeyDown)
  up.on('up:location:changed', hide)
  up.on('up:layer:location:changed', updateCurrent)
  up.on('convert:experiences:updated', updateCurrent)
  up.on('click', onBodyClick)

  updateCurrent()

  return () => {
    clearTimeout(hoverCloseTimeout)
    up.off('up:location:changed', hide)
    up.off('up:layer:location:changed', updateCurrent)
    up.off('convert:experiences:updated', updateCurrent)
    up.off('click', onBodyClick)
  }
})
